/**
 * This file respects https://github.com/redux-utilities/flux-standard-action
 */

import * as Sentry from '@sentry/browser';

import { ThunkAction } from '@tmapy/redux';
import { authGlobals, clearCredentials, parseJwt } from '@tmapy/auth';
import type { CallApiResult } from '@tmapy/beetlejuice-utils';

import type { AccessPolicies } from '../lib/acl';

import type { ExtrasTokenObtainPairResponse } from '../openapi/types';
import { routeToUrl } from '../routes';
import { tokenRefreshCreate } from '../openapi/operations/tokenRefreshCreate';
import { twistLoginCreateOperation } from '../openapi/operations/twistLoginCreate';
import { cobraWhoamiCreate } from '../openapi/operations/cobraWhoamiCreate';

import { getUserPolicies } from './policies';
// TWIST login DMX uses this scheduler instead of Keycloak's
import { scheduleTwistRefreshByToken, unscheduleTwistRefresh } from './twistRefreshScheduler';
import {
  AUTH_USER_SUCCESS,
  AUTH_USER_FAILURE,
  AUTH_LOGOUT,
  LOCALSTORAGE_REFRESH_TOKEN_KEY,
  AUTH_SET_POLICIES,
} from './constants';

// Fix OpenAPI type
declare module '../openapi/types/CobraWhoami' {
  interface CobraWhoamiResponse {
    user: any;
  }
}

/**
 * Real response, OpenAPI is lying on this endpoint
 */
interface TokenResponse {
  access: string;
  refresh: string;
  user: ExtrasTokenObtainPairResponse;
}

/**
 * Real response, OpenAPI is lying on this endpoint
 */
interface TokenRefreshResponse {
  access: string;
  refresh?: string;
}

const actionAuthUserSuccess =
  (data: TokenResponse): ThunkAction<Promise<void>> =>
  async (dispatch) => {
    authGlobals.token = data.access;
    authGlobals.refresh_token = data.refresh;
    localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN_KEY, data.refresh);

    Sentry.setUser(data.user as any);

    await dispatch(actionFetchPolicies());

    dispatch({
      type: AUTH_USER_SUCCESS,
      data: {
        ...data.user,
        token: data.access,
      },
    } as const);
  };

/**
 * T-WIST counterpart of `clearCredentials` from `@tmapy/auth`, usable outside a dispatch.
 */
export const clearTwistCredentials = () => {
  localStorage.removeItem(LOCALSTORAGE_REFRESH_TOKEN_KEY);
  authGlobals.token = null;
  authGlobals.refresh_token = null;
  unscheduleTwistRefresh();
};

/**
 * TWIST logout
 * Usage:
 * ```
 * dispatch(logout());
 * ```
 */
export const twistLogout = (): ThunkAction<Promise<void>> => async (dispatch) => {
  clearTwistCredentials();

  Sentry.setUser(null);

  await dispatch(actionFetchPolicies());

  dispatch({ type: AUTH_LOGOUT });
};

export const twistLogin =
  (token: string): ThunkAction =>
  async (dispatch, getState) => {
    const {
      app: { config },
    } = getState();
    try {
      const response = await fetch(`${config.server}${twistLoginCreateOperation().path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
        }),
      });

      if (!response.ok) {
        console.error('[AUTH]: T-WIST login failed', response.status, await response.text());
        dispatch({ type: AUTH_USER_FAILURE });
        await dispatch(actionFetchPolicies());
        return;
      }

      const json = (await response.json()) as TokenResponse;
      scheduleTwistRefreshByToken(dispatch, parseJwt(json.access));
      await dispatch(actionAuthUserSuccess(json));
    } catch (e) {
      console.error(e);
      window.history.replaceState(null, '', routeToUrl.error());
    }
  };

export const actionFetchPolicies = (): ThunkAction<Promise<AccessPolicies>> => async (dispatch) => {
  const payload = await getUserPolicies();
  dispatch({
    type: AUTH_SET_POLICIES,
    payload,
  });
  return payload;
};

export const cobraLogin =
  (access_token: string): ThunkAction =>
  async (dispatch) => {
    try {
      const response = await cobraWhoamiCreate({
        request: {
          cobra_access_token: access_token,
        },
      });

      if (response.ok) {
        const user = response.data.user;

        Sentry.setUser(user as any);

        dispatch({
          type: AUTH_USER_SUCCESS,
          data: {
            ...user,
            token: access_token,
          },
        });
      } else {
        // not called as an action, called only for side effects
        clearCredentials();

        dispatch({
          type: AUTH_USER_FAILURE,
        });
      }

      await dispatch(actionFetchPolicies());
    } catch (e) {
      console.error(e);
      window.history.replaceState(null, '', routeToUrl.error());
    }
  };

export const actionTwistTokenRefresh =
  (): ThunkAction<Promise<boolean>> =>
  async (dispatch): Promise<boolean> => {
    if (!authGlobals.refresh_token) {
      return false;
    }

    try {
      const response = (await tokenRefreshCreate({
        request: { refresh: authGlobals.refresh_token },
      })) as any as CallApiResult<TokenRefreshResponse>;

      if (response.ok) {
        authGlobals.token = response.data.access;
        const refreshToken = response.data.refresh;
        if (refreshToken) {
          authGlobals.refresh_token = refreshToken;
          localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN_KEY, refreshToken);
          scheduleTwistRefreshByToken(dispatch, parseJwt(refreshToken));
        }
        scheduleTwistRefreshByToken(dispatch, parseJwt(response.data.access));
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error(err);
      return false;
    }
  };
